// Appointments with a time — book them, see what's coming up, and write up
// what the vet said afterwards. Health → Vet visits shows the list; the
// sheet opens from anywhere (Today's Up next, the care plan).

import { CalendarClock, CalendarPlus, MapPin, NotebookPen, Trash2, X } from 'lucide-react';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { currentPerson } from '../../data/actions.ts';
import { cancelAppointment, deleteAppointment, saveAppointment, skipWriteUp } from '../../data/appointments.ts';
import { devicePrefs } from '../../data/device.ts';
import { useCollection, useDevice, useHousehold, useNow } from '../../data/hooks.ts';
import { uid } from '../../data/ids.ts';
import { APPOINTMENT_KINDS, apptMinutes, apptPlace, countdown, kindLabel, needsWriteUp, upcomingAppointments, whenLabel } from '../../domain/appointments.ts';
import { careState, isDateMilestone, openCare } from '../../domain/care.ts';
import { toISODate } from '../../domain/time.ts';
import type { Appointment, AppointmentKind } from '../../domain/types.ts';
import { Button, Card, CardHeader, Field, Segmented, Sheet } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';
import { openVetVisit } from './VetVisits.tsx';

// --- Open the sheet from anywhere ----------------------------------------------------------

let editing: { appt: Appointment; isNew: boolean } | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function openAppointment(draft: Partial<Appointment> = {}, existing?: Appointment) {
  if (devicePrefs().guest) return;
  if (existing) editing = { appt: existing, isNew: false };
  else {
    // Default: tomorrow at 10:00.
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(10, 0, 0, 0);
    editing = { appt: { id: uid(), at: t.getTime(), kind: 'vet', title: 'Vet check-up', by: currentPerson(), ...draft }, isNew: true };
  }
  emit();
}

function close() {
  editing = null;
  emit();
}

export function AppointmentHost() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => editing,
  );
  if (!s) return null;
  return <AppointmentSheet key={s.appt.id} initial={s.appt} isNew={s.isNew} />;
}

/** Write up a finished vet appointment as a visit, prefilled. */
export function writeUp(a: Appointment, practice?: string) {
  openVetVisit({ at: a.at, kind: 'visit', contactId: a.contactId, practice, reason: a.title, careDone: a.careIds, appointmentId: a.id });
}

// --- Coming up (Health → Vet visits) ---------------------------------------------------------

export function AppointmentsCard() {
  const guest = !!useDevice().guest;
  const appts = useCollection('appointments');
  const contacts = useCollection('contacts');
  const now = useNow(60_000);
  const upcoming = useMemo(() => upcomingAppointments(appts, now), [appts, now]);
  const toWrite = useMemo(() => needsWriteUp(appts, now), [appts, now]);
  return (
    <Card>
      <CardHeader
        title="Appointments"
        icon={CalendarClock}
        sub={upcoming.length ? undefined : 'Nothing booked. Add the vet, puppy class or groomer with a time — you’ll both get a reminder the evening before and an hour before.'}
        action={
          !guest && (
            <Button size="sm" variant="primary" icon={CalendarPlus} onClick={() => openAppointment()}>
              Book
            </Button>
          )
        }
      />
      {toWrite.map((a) => (
        <div key={a.id} className="callout tone-warn appt-writeup">
          <NotebookPen aria-hidden />
          <div>
            <strong>How did it go? {a.title}, {whenLabel(a.at, now)}</strong>
            <span className="small">Write down what they said, what was given and the cost while it’s fresh.</span>
            {!guest && (
              <div className="row">
                <Button size="sm" variant="primary" onClick={() => writeUp(a, apptPlace(a, contacts))}>
                  Write it up
                </Button>
                <Button size="sm" variant="ghost" onClick={() => skipWriteUp(a)}>
                  Nothing to note
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
      {upcoming.length > 0 && (
        <ul className="appt-list">
          {upcoming.map((a) => {
            const place = apptPlace(a, contacts);
            const soon = countdown(a.at, now);
            return (
              <li key={a.id}>
                <button type="button" className="appt-row" onClick={guest ? undefined : () => openAppointment({}, a)} disabled={guest}>
                  <span className="appt-when">
                    <strong>{new Date(a.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</strong>
                    <span>{new Date(a.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                  </span>
                  <span className="appt-text">
                    <strong>{a.title}</strong>
                    <span className="muted small">
                      {kindLabel(a.kind)}
                      {place ? ` · ${place}` : ''}
                      {soon ? ` · ${soon}` : ''}
                    </span>
                    {a.notes && <span className="small">{a.notes}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// --- The sheet ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (t: number) => toISODate(t);
const localTime = (t: number) => `${pad(new Date(t).getHours())}:${pad(new Date(t).getMinutes())}`;
function combine(date: string, time: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh || 0, mm || 0).getTime();
}

function AppointmentSheet({ initial, isNew }: { initial: Appointment; isNew: boolean }) {
  const contacts = useCollection('contacts');
  const care = useCollection('care');
  const household = useHousehold();
  const [a, setA] = useState<Appointment>(initial);
  const [date, setDate] = useState(localDate(initial.at));
  const [time, setTime] = useState(localTime(initial.at));
  const set = (patch: Partial<Appointment>) => setA((x) => ({ ...x, ...patch }));
  const places = contacts.filter((c) => ['vet', 'emergency-vet', 'groomer', 'trainer', 'other'].includes(c.role));

  // Care jobs this could be for: open ones due within six weeks of the date.
  const candidates = useMemo(() => {
    const at = combine(date, time);
    return openCare(care).filter((c) => ['vaccination', 'worming', 'flea', 'vet', 'weigh', 'grooming', 'admin'].includes(c.kind) && careState(c) !== 'unscheduled' && !isDateMilestone(c) && (!c.due || Math.abs(new Date(c.due).getTime() - at) < 42 * 86_400_000));
  }, [care, date, time]);
  const toggleCare = (id: string) => set({ careIds: (a.careIds ?? []).includes(id) ? (a.careIds ?? []).filter((x) => x !== id) : [...(a.careIds ?? []), id] });
  const toggleWho = (id: string) => set({ who: (a.who ?? []).includes(id) ? (a.who ?? []).filter((x) => x !== id) : [...(a.who ?? []), id] });

  const save = () => {
    const at = combine(date, time);
    if (!Number.isFinite(at)) return toast({ message: 'Pick a date and time', tone: 'bad' });
    const appt: Appointment = { ...a, at, title: a.title.trim() || kindLabel(a.kind), place: a.place?.trim() || undefined, notes: a.notes?.trim() || undefined, who: a.who?.length ? a.who : undefined, careIds: a.careIds?.length ? a.careIds : undefined };
    saveAppointment(appt, isNew ? undefined : initial);
    toast({ message: isNew ? 'Booked' : 'Appointment updated', detail: `${appt.title}, ${whenLabel(at, Date.now())}${appt.careIds?.length ? ' · care plan moved to that day' : ''}`, tone: 'good' });
    close();
  };

  const pickKind = (kind: AppointmentKind) => {
    const was = kindLabel(a.kind);
    const defaults: Record<AppointmentKind, string> = { vet: 'Vet check-up', vaccination: 'Vaccination', class: 'Puppy class', groomer: 'Groomer', other: '' };
    set({ kind, title: !a.title || a.title === was || Object.values(defaults).includes(a.title) ? defaults[kind] : a.title, minutes: undefined });
  };

  return (
    <Sheet
      open
      wide
      onClose={close}
      title={isNew ? 'Book an appointment' : 'Appointment'}
      footer={
        <>
          {!isNew && (
            <>
              <Button
                variant="ghost"
                icon={Trash2}
                onClick={() => {
                  deleteAppointment(initial);
                  toast({ message: 'Appointment deleted', tone: 'good' });
                  close();
                }}
              >
                Delete
              </Button>
              {!initial.cancelled && (
                <Button
                  variant="ghost"
                  icon={X}
                  onClick={() => {
                    cancelAppointment(initial);
                    toast({ message: 'Appointment cancelled', tone: 'good' });
                    close();
                  }}
                >
                  Cancelled
                </Button>
              )}
            </>
          )}
          <Button variant="primary" onClick={save}>
            {isNew ? 'Book' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="form">
        <Field label="What">
          <Segmented value={a.kind} onChange={pickKind} options={APPOINTMENT_KINDS.map((k) => ({ value: k.kind, label: k.label }))} />
        </Field>
        <Field label="Title">
          <input className="input" value={a.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. 2nd vaccination" />
        </Field>
        <div className="form-grid">
          <Field label="Date">
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Time">
            <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
          <Field label="Takes about" hint="Minutes">
            <input className="input" type="number" inputMode="numeric" min={5} step={5} value={apptMinutes(a)} onChange={(e) => set({ minutes: Number(e.target.value) || undefined })} />
          </Field>
        </div>
        <Field label="Where">
          <select className="input" value={a.contactId ?? ''} onChange={(e) => set({ contactId: e.target.value || undefined })}>
            <option value="">{places.length ? 'Somewhere else (type below)' : 'Type below — or add your vet under Contacts'}</option>
            {places.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        {!a.contactId && (
          <Field label="Place">
            <input className="input" value={a.place ?? ''} onChange={(e) => set({ place: e.target.value })} placeholder="e.g. Kelvingrove puppy class" />
          </Field>
        )}
        {household.people.length > 1 && (
          <Field label="Who’s going?" hint="Reminders go to them — none ticked means both of you.">
            <div className="chips">
              {household.people.map((p) => (
                <button key={p.id} type="button" className={`chip${(a.who ?? []).includes(p.id) ? ' on' : ''}`} onClick={() => toggleWho(p.id)}>
                  {p.name}
                </button>
              ))}
            </div>
          </Field>
        )}
        {(a.kind === 'vet' || a.kind === 'vaccination' || a.kind === 'groomer') && candidates.length > 0 && (
          <Field label="For" hint="Ticked care-plan items move to this day, and get ticked off when you write up the visit.">
            <div className="chips">
              {candidates.map((c) => (
                <button key={c.id} type="button" className={`chip${(a.careIds ?? []).includes(c.id) ? ' on' : ''}`} onClick={() => toggleCare(c.id)}>
                  {c.title}
                </button>
              ))}
            </div>
          </Field>
        )}
        <Field label="Remember to take / ask">
          <input className="input" value={a.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} placeholder="e.g. Vaccination card, a stool sample, treats" />
        </Field>
        <p className="muted small">
          <MapPin aria-hidden className="inline-icon" /> Shows on Today, in your calendars (if you’ve added Arlo’s), and both phones get a reminder the evening before and an hour before.
        </p>
      </div>
    </Sheet>
  );
}
